<template>
  <base-slide-over ref="approveWithChangesSlideover" title="Refill Approval" size="5xl" style="z-index: 10000">
    <base-content>
      <div v-if="isLoading" class="tw-flex tw-justify-center tw-py-5">
        <base-spinner />
      </div>
      <div v-else>
        <edit-business-member-prescription-medications
          :allowed-medications="allowedMedications"
          :max-medications="prescription.businessProgram ? prescription.businessProgram.maxMedications : 0"
          :edit-medications.sync="editMedications"
          :medications-label="dynamicLabel"
          hide-save
        />
        <div class="tw-pb-2">
          <edit-business-member-prescription-rx-items :edit-rx-items.sync="editRxItems" :allowed-rx-items="allowedRxItems" hide-save />
        </div>
      </div>
      <inline-error id="approve-changes-error" :title="errorTitle" :message="errorMessage" :error="error" />
    </base-content>
    <template #fixed-bottom>
      <div class="tw-p-3 tw-flex tw-justify-end">
        <base-button type="flat" class="tw-w-auto tw-mr-2" @click="hide()">
          Cancel
        </base-button>
        <base-button class="tw-w-auto" @click="approveRxAuth" :loading="isSaving"> Approve{{ changesMade ? " with Changes" : "" }} </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import EditBusinessMemberPrescriptionMedications from "@/views/business/member/prescriptions/components/EditBusinessMemberPrescriptionMedications";
import EditBusinessMemberPrescriptionRxItems from "@/views/business/member/prescriptions/components/EditBusinessMemberPrescriptionRxItems";
import InlineError from "@/components/InlineError";
import { orderBy, isEqual } from "lodash";
import { mapGetters } from "vuex";

export default {
  name: "ApproveRxAuthWithChanges",
  components: {
    EditBusinessMemberPrescriptionMedications,
    InlineError,
    EditBusinessMemberPrescriptionRxItems,
  },
  props: {
    businessId: String,
    rxItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      todoItemId: null,
      userId: null,
      businessPrescriptionId: null,
      prescription: {},
      editMedications: [],
      editRxItems: [],
      originalMedications: [],
      originalRxItems: [],
      errorTitle: "",
      errorMessage: "",
      error: null,
      currentComponent: null,
    };
  },
  computed: {
    ...mapGetters("location", ["location", "locations"]),
    allowedMedications() {
      return this.prescription.businessProgram
        ? this.prescription.businessProgram.allowedMedications
            .map((am) => ({
              label: am.name.value,
              value: am.id,
              productGroup: am.productGroup,
            }))
            .filter((m) => !this.editMedications.find((em) => em.value === m.value))
        : [];
    },
    changesMade() {
      // Check for changes in Medications
      const newMedicationValues = orderBy(this.editMedications.map((m) => m.value).filter((m) => m));
      const originalMedicationValues = orderBy(this.originalMedications.filter((m) => m));
      const medicationsChanged = !isEqual(newMedicationValues, originalMedicationValues);
      // Check for changes in Rx Items
      const newRxItemValues = orderBy(this.editRxItems.map((item) => item.value));
      const originalRxItemValues = orderBy(this.originalRxItems.map((item) => item.value));
      const rxItemsChanged = !isEqual(newRxItemValues, originalRxItemValues);

      return medicationsChanged || rxItemsChanged;
    },
    dynamicLabel() {
      const label = this.prescription && this.prescription.businessProgram && this.prescription.businessProgram.programFieldLabel;
      return label ? label.charAt(0).toUpperCase() + label.slice(1) : "Medication";
    },
    allowedRxItems() {
      const currentProgram = this.prescription.businessProgram;

      if (!currentProgram || !this.rxItems || this.rxItems.length === 0) {
        return [];
      }

      const currentProgramId = currentProgram.id;
      const filtered = this.rxItems.filter((rxItem) => {
        const itemFilterType = rxItem.programFilterType;
        let itemProgramsIds = [];

        if (rxItem.programsId && typeof rxItem.programsId === "string") {
          try {
            itemProgramsIds = JSON.parse(rxItem.programsId);
          } catch (e) {
            itemProgramsIds = [];
          }
        } else if (Array.isArray(rxItem.programsId)) {
          itemProgramsIds = rxItem.programsId;
        }

        let isVisible = false;
        if (itemFilterType === null || itemFilterType === "all") {
          isVisible = true;
        } else if (itemFilterType === "include") {
          isVisible = itemProgramsIds.includes(currentProgramId);
        } else if (itemFilterType === "exclude") {
          isVisible = !itemProgramsIds.includes(currentProgramId);
        } else {
          isVisible = true;
        }
        // Exclude items already selected in editRxItems
        return isVisible && !this.editRxItems.find((eRi) => eRi.value === rxItem.businessItemCategoryId);
      });
      const mappedItems = filtered.map((item) => ({
        label: item.name,
        value: item.businessItemCategoryId,
        productGroup: item.productGroup,
      }));
      return mappedItems;
    },
  },
  methods: {
    show(todoItemId, userId, businessPrescriptionId) {
      this.todoItemId = todoItemId;
      this.userId = userId;
      this.businessPrescriptionId = businessPrescriptionId;
      this.$refs.approveWithChangesSlideover.show();
      this.currentComponent = "BusinessMemberPrescriptions";
      this.getPrescription();
    },
    hide() {
      this.reset();
      this.$refs.approveWithChangesSlideover.hide();
    },
    reset() {
      this.todoItemId = null;
      this.userId = null;
      this.editMedications = [];
      this.originalMedications = [];
      this.editRxItems = [];
      this.originalRxItems = [];
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
    },
    async getPrescription() {
      try {
        this.isLoading = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/prescriptions/${this.businessPrescriptionId}`);
        this.prescription = response.data.prescription;
        this.editMedications = orderBy(this.prescription.medications, ["sortOrder"]).map((m) => ({
          label: m.businessItemCategory.name.value,
          value: m.businessItemCategory.id,
          productGroup: m.businessItemCategory.productGroup,
        }));
        this.originalMedications = this.editMedications.map((m) => m.value).filter((m) => m);

        this.editRxItems = orderBy(this.prescription.rxItems, ["sortOrder"]).map((item) => ({
          label: item.businessItemCategory && item.businessItemCategory.name ? item.businessItemCategory.name.value : "",
          value: item.businessItemCategory.id,
          productGroup: item.businessItemCategory.productGroup,
        }));
        // Deep copy to ensure independence for originalRxItems
        this.originalRxItems = this.editRxItems.map((item) => ({ ...item }));
      } finally {
        this.isLoading = false;
      }
    },
    approveRxAuth() {
      this.isSaving = true;
      const newMedicationIds = this.editMedications.map((m) => m.value).filter((id) => id);
      const newRxItemIds = this.editRxItems.map((rx) => rx.value).filter((id) => id);

      // Prepare the payload, sending separate arrays as seen in BusinessMemberPrescriptionDetails
      const payload = {
        businessItemCategoryIds: newMedicationIds,
        rxItemCategoryIds: newRxItemIds,
      };
      this.$axios
        .post(`/businesses/${this.businessId}/todo/${this.todoItemId}/approve?isWithChanges=${this.changesMade ? "true" : "false"}`, payload)
        .then(() => {
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "Authorization Approved",
            message: `Your approval was submitted. Thank you.`,
          });
          this.EventBus.$emit("activitiesUpdated");
          this.hide();
        })
        .catch((err) => {
          this.errorTitle = `Error submitting approval`;
          this.errorMessage = "Sorry, we had trouble submitting this approval. The technicians have been notified. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>
